import { ArpMedia, ArpMediaType } from '@arpeggi/shared'
import { useCombobox, useMultipleSelection } from 'downshift'
import { isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import usePrevious from '../../../hooks/usePrevious'
import { useArpMedias } from '../../../queries/arpMedia'
import { capitalizeFirstLetter } from '../../../utils/strings'
import { Box, IconButton, Spacer } from '../../core/Components'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import { CloseIcon } from '../../core/icons'
import Text from '../../core/Text/Text'
import { TextInput } from '../../core/TextInput'
import { StyledMenuSelect, StyledMenuSelectItem } from '../../internal/components/UserSelect'
import { Badge } from './common'

interface Props {
  types: ArpMediaType[]
  onChange: (arpMedias: ArpMedia[]) => void
  defaultSelectedArp?: ArpMedia[]
  errorMessage?: string
}

export const ArpMediaMultiSelect: React.FC<Props> = ({
  types,
  onChange,
  defaultSelectedArp = [],
  errorMessage
}) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 250)
  const arpMediaQuery = useArpMedias({ perPage: 20, search: debouncedSearch, type: types })
  const [selectedArpMedia, setSelectedArpMedia] = useState(defaultSelectedArp)
  const prevDefaults = usePrevious(defaultSelectedArp)
  const prevSelected = usePrevious(selectedArpMedia)

  useEffect(() => {
    if (!isEqual(prevSelected, selectedArpMedia)) {
      onChange(selectedArpMedia)
    } else if (!isEqual(prevDefaults, defaultSelectedArp)) {
      setSelectedArpMedia(defaultSelectedArp)
    }
  }, [prevSelected, selectedArpMedia, prevDefaults, defaultSelectedArp])

  const items = useMemo(
    () =>
      arpMediaQuery.data?.pages.flatMap((page) =>
        page.data.filter((x) => !selectedArpMedia.find((y) => y.id === x.id))
      ) || [],
    [arpMediaQuery.data?.pages, selectedArpMedia]
  )

  const { removeSelectedItem, selectedItems } = useMultipleSelection({
    selectedItems: selectedArpMedia,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedArpMedia(newSelectedItems)
          break
        default:
          break
      }
    }
  })

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    reset
  } = useCombobox<ArpMedia>({
    onInputValueChange({ inputValue }) {
      setSearch(inputValue)
    },
    items,
    itemToString(item) {
      return item ? item.metadata.title : ''
    },
    defaultHighlightedIndex: 0,
    selectedItem: null,
    onStateChange({ inputValue: newInputValue, type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          setSelectedArpMedia([...selectedItems, newSelectedItem])
          break
        case useCombobox.stateChangeTypes.InputChange:
          setSearch(newInputValue)
          break
        default:
          break
      }
    }
  })

  const placeholder = types.length === 1 ? `${capitalizeFirstLetter(types[0])}s` : 'Arp Media'

  return (
    <>
      <FieldLabel {...getLabelProps()}>Select {placeholder}</FieldLabel>
      <Box {...getComboboxProps()}>
        <TextInput
          {...getInputProps()}
          placeholder={`Search ${placeholder}`}
          css={{ maxWidth: '100%', width: '400px' }}
        />
      </Box>
      <StyledMenuSelect {...getMenuProps()}>
        {isOpen &&
          items.map((item, index) => {
            const isSelected = selectedItem?.id === item.id

            return (
              <StyledMenuSelectItem
                key={item.id}
                {...getItemProps({ item, index })}
                highlighted={highlightedIndex === index}
                selected={isSelected}
              >
                <Text
                  size="14"
                  css={{ marginRight: '8px', fontWeight: isSelected ? '$bold' : 'initial' }}
                >
                  {item.metadata.title}
                </Text>
                <Text size="10" type="secondary">
                  {' '}
                  ({item.id})
                </Text>
                <Badge css={{ backgroundColor: '$background__secondary', marginLeft: '8px' }}>
                  {item.type}
                </Badge>
              </StyledMenuSelectItem>
            )
          })}
      </StyledMenuSelect>
      <FieldErrorLabel>{errorMessage}</FieldErrorLabel>
      {selectedItems.map((arpMedia) => (
        <Flex css={{ gap: '8px', alignItems: 'center', paddingTop: '4px', paddingBottom: '4px' }}>
          <Text>{arpMedia.id}</Text>
          <Text>{arpMedia.metadata.title}</Text>
          <Badge css={{ backgroundColor: '$background__secondary' }}>{arpMedia.type}</Badge>
          <Spacer />
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              removeSelectedItem(arpMedia)
            }}
          >
            <CloseIcon />
          </IconButton>
        </Flex>
      ))}
    </>
  )
}
